import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import { ChangeEvent, FC, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { uploadCountryVatRatesRequest } from 'redux/reducers/taxDuties/reducer';
import { getCountryVatRateUploadLoading } from 'redux/reducers/taxDuties/selectors';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import { TUploadRatesDialogProps } from './types';

/**
 * The endpoint also accepts a `data_type` form field, but swagger documents no accepted values for it
 * and the import works without one, so only the file is sent.
 */
export const UploadRatesDialog: FC<TUploadRatesDialogProps> = ({ open, onClose, listRequest }) => {
	const dispatch = useDispatch();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState('');

	const uploadLoading = useSelector(getCountryVatRateUploadLoading);

	const resetState = () => {
		setSelectedFile(null);
		setFileError('');
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
		setSelectedFile(event.target.files?.[0] ?? null);
		setFileError('');
	};

	const handleUpload = () => {
		if (!selectedFile) {
			setFileError('File is required');
			return;
		}

		const formData = new FormData();
		formData.append('File', selectedFile);

		dispatch(uploadCountryVatRatesRequest({ formData, listRequest }));
		resetState();
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{ '& .MuiDialog-paper': { width: '460px', maxWidth: '460px', borderRadius: '12px' } }}
		>
			<DialogTitle color="primary">Upload EU VAT Rates File</DialogTitle>
			<DialogContent sx={{ p: '12px 24px' }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Box>
						<input
							type="file"
							ref={fileInputRef}
							onChange={handleFileSelect}
							style={{ display: 'none' }}
						/>
						{!selectedFile ? (
							<Box
								onClick={() => fileInputRef.current?.click()}
								sx={{
									border: '2px dashed',
									borderColor: fileError ? 'error.main' : 'primary.main',
									borderRadius: '8px',
									p: 3,
									textAlign: 'center',
									cursor: 'pointer',
									'&:hover': { backgroundColor: 'action.hover' },
								}}
							>
								<CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
								<Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
									Click to upload file
								</Typography>
							</Box>
						) : (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: '8px',
									p: 2,
								}}
							>
								<Typography variant="body2" noWrap sx={{ flex: 1 }}>
									{selectedFile.name}
								</Typography>
								<Button
									size="small"
									onClick={() => {
										setSelectedFile(null);
										if (fileInputRef.current) fileInputRef.current.value = '';
									}}
								>
									<CloseIcon fontSize="small" />
								</Button>
							</Box>
						)}
						{fileError && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
								{fileError}
							</Typography>
						)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button
					variant="contained"
					disabled={uploadLoading}
					onClick={handleUpload}
					startIcon={uploadLoading ? <ButtonCircularProgress /> : undefined}
				>
					Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};
